import { useState } from "react";
import { useDelete, useList, useTranslate } from "@refinedev/core";
import {
  PlusCircleSolidIcon, ActionBar, Button, FormCard, SectionHeader, type Column } from "@fawkes/ui";
import { IndeelbareTabel } from "../components/IndeelbareTabel";

interface WassenSoort {
  id: number;
  naam: string;
  version?: number;
}

// jve-2-4 / jve-2-6 parity pass: the wassen-soort reference list (docasoft read). Composes the same
// @fawkes/ui list vocabulary (FormCard + SectionHeader + DataTable) as the other migrated lists so it
// reads like the Jmix `wassenSoortsDataGrid`. The Jmix grid also auto-scaffolds a `version` column, but
// that is the JPA optimistic-lock counter (infra) — the UI never renders it; it is only carried on the
// wire so an edit can round-trip it.
//
// wassen-soort-write: the Jmix list-view toolbar (Create / Edit / Remove) is now reproduced — a header
// "+ Nieuw" opens the detail in create mode (id sentinel "new"), each row has Bewerken (open detail) and
// Verwijderen. Delete relays the server 409 koppelingen message verbatim (FK still in use). The
// ROLE_PRODUCT_EDIT authority is enforced server-side (POST/PUT/DELETE → 403); the affordances stay
// enabled, matching the producten screens (no client authority signal).
export function WassenSoortList({ onOpen }: { onOpen?: (id: string) => void } = {}) {
  const t = useTranslate();
  const { data, isLoading, isError, refetch } = useList<WassenSoort>({ resource: "wassen-soorten" });
  const { mutate: remove, isLoading: deleting } = useDelete();
  const [notice, setNotice] = useState<string | null>(null);

  const deleteRow = (w: WassenSoort) => {
    if (!window.confirm(t("wassenSoortListView.delete.confirm"))) return;
    setNotice(null);
    remove(
      { resource: "wassen-soorten", id: w.id, successNotification: false, errorNotification: false },
      {
        onSuccess: () => refetch(),
        onError: (err) => {
          const e = err as { statusCode?: number; message?: string };
          // A 409 body is the server koppelingen message ("nog in gebruik") — surface it verbatim.
          setNotice(e.statusCode === 409 && e.message ? e.message : t("wassenSoortListView.delete.error"));
        },
      },
    );
  };

  const columns: Column<WassenSoort>[] = [
    { header: t("wassenSoortListView.column.naam"), cell: (w) => w.naam },
  ];

  if (onOpen) {
    columns.push({
      header: "",
      cell: (w) => (
        <span className="fawkes-row-actions">
          <Button variant="secondary" onClick={() => onOpen(String(w.id))}>
            {t("wassenSoortListView.edit")}
          </Button>
          <Button variant="secondary" onClick={() => deleteRow(w)} disabled={deleting}>
            {t("wassenSoortListView.delete")}
          </Button>
        </span>
      ),
    });
  }

  return (
    <FormCard>
      <SectionHeader>{t("wassenSoortListView.title")}</SectionHeader>
      {onOpen && (
        <ActionBar>
          <Button variant="primary" onClick={() => onOpen("new")}>
            <PlusCircleSolidIcon width={18} height={18} /> {t("wassenSoortListView.new")}
          </Button>
        </ActionBar>
      )}
      {notice && <p className="fawkes-error">{notice}</p>}
      {isLoading ? (
        <p className="fawkes-muted">{t("common.loading")}</p>
      ) : isError ? (
        <p className="fawkes-error">{t("wassenSoortListView.loadFailed")}</p>
      ) : (
        <IndeelbareTabel tabelKey="wassen-soort-list"
          columns={columns}
          rows={data?.data ?? []}
          getRowKey={(w) => String(w.id)}
          empty={t("wassenSoortListView.empty")}
        />
      )}
    </FormCard>
  );
}
